import * as THREE from "three";
import { canvas2d } from "./textures";
import { toon } from "./toon";

// The shops along a street spot's building fronts (#152): a sign board over
// each shopfront with a made-up local business's name, so the blocks read as
// a real downtown street. Invented names only, no real brands or logos.
// Render only; `buildStreet` (level-meshes.ts) hangs one on every building,
// and every building has its own shop, so no name repeats (#167).

export interface Shop {
  name: string;
  /** The line under the name: what it sells. */
  line: string;
  bg: string;
  ink: string;
  /** Its awning's color. */
  awning: string;
  /** A green cross beside the name (a pharmacy). */
  cross?: boolean;
  /** How many units wide its building is (#186: the coffee shop takes two); one if left out. */
  span?: number;
}

export const SHOPS: readonly Shop[] = [
  { name: "THREADBARE", line: "CLOTHING CO.", bg: "#1f2a36", ink: "#f2e9d8", awning: "#1f2a36" },
  { name: "WIRED UP", line: "PHONE REPAIR", bg: "#2f6fa8", ink: "#ffffff", awning: "#5aa0d8" },
  { name: "SLICE OF LIFE", line: "PIZZA BY THE SLICE", bg: "#b8322a", ink: "#fff3d6", awning: "#b8322a" },
  { name: "SPIN CYCLE", line: "LAUNDROMAT", bg: "#2f6fa8", ink: "#ffffff", awning: "#5aa0d8" },
  { name: "FIRST MERCHANTS BANK", line: "SINCE 1921", bg: "#e9e4d8", ink: "#23324a", awning: "#23324a" },
  { name: "NOLLIE & SONS", line: "SKATE SHOP", bg: "#151515", ink: "#ffd23f", awning: "#151515" },
  { name: "CLEAN CUTS", line: "BARBERSHOP", bg: "#f2f2f2", ink: "#b8322a", awning: "#2f4f8f" },
  { name: "DOG-EARED", line: "USED BOOKS", bg: "#2e5e3a", ink: "#f4efe0", awning: "#3f7a4c" },
  { name: "MAIN ST. HARDWARE", line: "KEYS CUT", bg: "#d9772b", ink: "#1d1d1d", awning: "#c4621d" },
  { name: "GOLDEN DUMPLING", line: "NOODLES & DIM SUM", bg: "#b8322a", ink: "#fff3d6", awning: "#b8322a" },
  { name: "THE GREEN GROCER", line: "FRESH PRODUCE", bg: "#2e5e3a", ink: "#f4efe0", awning: "#3f7a4c" },
  { name: "SOLE SEARCHING", line: "SHOES & REPAIRS", bg: "#5a3d2b", ink: "#f0d9b5", awning: "#7a5236" },
  { name: "PAGE ONE PRINTING", line: "COPIES . SIGNS", bg: "#2f6fa8", ink: "#ffffff", awning: "#5aa0d8" },
  { name: "ROSIE'S DINER", line: "BREAKFAST ALL DAY", bg: "#f2d24a", ink: "#b8322a", awning: "#b8322a" },
  { name: "FOCAL POINT", line: "OPTICIANS", bg: "#1f7a7a", ink: "#ffffff", awning: "#2a9a9a" },
  { name: "BLOOM & STEM", line: "FLORIST", bg: "#f3c6cf", ink: "#7a2140", awning: "#d9849a" },
  { name: "TWO WHEELS", line: "BIKE REPAIR", bg: "#151515", ink: "#ffd23f", awning: "#151515" },
  { name: "WAXWORKS", line: "RECORDS & TAPES", bg: "#3b2a5a", ink: "#f6d6ff", awning: "#5a3f86" },
  { name: "TAQUERIA LUNA", line: "TACOS . BURRITOS", bg: "#d9772b", ink: "#1d1d1d", awning: "#c4621d" },
  { name: "CITYWIDE CLEANERS", line: "DRY CLEANING", bg: "#2f6fa8", ink: "#ffffff", awning: "#5aa0d8" },
  { name: "PAWS & CLAWS", line: "PET SUPPLIES", bg: "#1f7a7a", ink: "#ffffff", awning: "#2a9a9a" },
  { name: "KNEAD TO KNOW", line: "BAKERY", bg: "#f3e6c8", ink: "#6b4a36", awning: "#c79a5a" },
  { name: "BRIGHT SMILE", line: "DENTAL CARE", bg: "#f4f6f2", ink: "#1d7a46", awning: "#1d7a46" },
  { name: "SECOND ACT", line: "VINTAGE CLOTHING", bg: "#3b2a5a", ink: "#f6d6ff", awning: "#5a3f86" },
  { name: "THE TOOL LIBRARY", line: "HARDWARE RENTAL", bg: "#d9772b", ink: "#1d1d1d", awning: "#c4621d" },
  { name: "MERIDIAN MARKET", line: "DELI & GROCERY", bg: "#2e5e3a", ink: "#f4efe0", awning: "#3f7a4c" },
  { name: "STITCH IN TIME", line: "TAILOR", bg: "#1f2a36", ink: "#f2e9d8", awning: "#1f2a36" },
  { name: "PIXEL PALACE", line: "ARCADE", bg: "#3b2a5a", ink: "#f6d6ff", awning: "#5a3f86" },
  { name: "ON THE MEND", line: "PHYSIOTHERAPY", bg: "#1f7a7a", ink: "#ffffff", awning: "#2a9a9a" },
  { name: "HOT WOK", line: "CHINESE TAKEOUT", bg: "#b8322a", ink: "#fff3d6", awning: "#b8322a" },
  { name: "SPRUCE", line: "HOME & GARDEN", bg: "#2e5e3a", ink: "#f4efe0", awning: "#3f7a4c" },
  { name: "NORTHSIDE CREDIT UNION", line: "ATM INSIDE", bg: "#e9e4d8", ink: "#23324a", awning: "#23324a" },
  { name: "INK WELL", line: "TATTOO STUDIO", bg: "#151515", ink: "#ffd23f", awning: "#151515" },
  { name: "BUTTON & BOW", line: "KIDS' CLOTHES", bg: "#f3c6cf", ink: "#7a2140", awning: "#d9849a" },
  { name: "BEAN THERE", line: "COFFEE & BAGELS", bg: "#4a3326", ink: "#f0d9b5", awning: "#6b4a36", span: 2 },
  { name: "LAST CHAPTER", line: "COMICS & GAMES", bg: "#f2d24a", ink: "#b8322a", awning: "#b8322a" },
  { name: "CORNER PHARMACY", line: "OPEN 7 DAYS", bg: "#f4f6f2", ink: "#1d7a46", awning: "#1d7a46", cross: true },
  { name: "SALT & VINEGAR", line: "FISH & CHIPS", bg: "#1f2a36", ink: "#f2e9d8", awning: "#1f2a36" },
  { name: "CUT ABOVE", line: "HAIR SALON", bg: "#f3c6cf", ink: "#7a2140", awning: "#d9849a" },
  { name: "THE PRINT SHOP", line: "FRAMING & ART", bg: "#5a3d2b", ink: "#f0d9b5", awning: "#7a5236" },
  { name: "FRESH PRESS", line: "JUICE BAR", bg: "#2e5e3a", ink: "#f4efe0", awning: "#3f7a4c" },
  { name: "LOCKSMITH LOU", line: "KEYS . LOCKS . SAFES", bg: "#d9772b", ink: "#1d1d1d", awning: "#c4621d" },
  { name: "BIG TOP TOYS", line: "TOYS & GAMES", bg: "#f2d24a", ink: "#b8322a", awning: "#b8322a" },
  { name: "WELL READ", line: "NEWSAGENT", bg: "#1f2a36", ink: "#f2e9d8", awning: "#1f2a36" },
  { name: "CORNER STORE", line: "OPEN LATE", bg: "#b8322a", ink: "#fff3d6", awning: "#b8322a" },
  { name: "SWEET TOOTH", line: "CANDY & ICE CREAM", bg: "#f3c6cf", ink: "#7a2140", awning: "#d9849a" },
  { name: "UPTOWN LAUNDRY", line: "WASH & FOLD", bg: "#1f7a7a", ink: "#ffffff", awning: "#2a9a9a" },
  { name: "POTTER'S WHEEL", line: "CERAMICS STUDIO", bg: "#5a3d2b", ink: "#f0d9b5", awning: "#7a5236" },
  { name: "GRIND HOUSE", line: "SANDWICHES", bg: "#151515", ink: "#ffd23f", awning: "#151515" },
  { name: "HARBOR INSURANCE", line: "AUTO . HOME . LIFE", bg: "#e9e4d8", ink: "#23324a", awning: "#23324a" },
];

/** A sign board's size (m): wide enough to read from across the street, under the first floor's windows. */
/** An empty unit's sign (#186): not a shop, so it's not in `SHOPS`; its cell comes after theirs in the atlas. */
export const COMING_SOON_SIGN: Shop = { name: "COMING SOON", line: "UNIT AVAILABLE", bg: "#f4efe0", ink: "#1d1d1d", awning: "#1d1d1d" };

/** Every sign in the atlas: the shops', then the empty unit's (`COMING_SOON_CELL`). */
const SIGNS: readonly Shop[] = [...SHOPS, COMING_SOON_SIGN];
export const COMING_SOON_CELL = SHOPS.length;

export const SIGN_W = 4.4;
export const SIGN_H = 1.1;

/** How far the street runs on past each end barrier (m); the fog swallows the rest. */
export const STREET_RUN = 80;

/** One building along a street spot (#115), on the fence line of the north (-1) or south (1) side. */
export interface StreetFront {
  side: -1 | 1;
  /** Its west end and width along the street (m). */
  x: number;
  w: number;
  h: number;
  /** Its shop, an index into `SHOPS`. */
  shop: number;
}

/**
 * The buildings along both sides of a street spot whose fence runs `hx` each
 * way: north side first, west to east, out to `STREET_RUN` past the barriers.
 * A fixed layout, so every build looks the same; each gets the next shop.
 */
export function streetFronts(hx: number): StreetFront[] {
  const end = hx + STREET_RUN;
  const out: StreetFront[] = [];
  let k = 0;
  for (const side of [-1, 1] as const) {
    for (let x = -end; x < end; k++) {
      const w = (9 + ((k * 5) % 7)) * (SHOPS[k]?.span ?? 1);
      out.push({ side, x, w, h: 11 + ((k * 7) % 19), shop: k });
      x += w;
    }
  }
  if (k > SHOPS.length) throw new Error(`streetFronts: ${k} buildings but only ${SHOPS.length} shops`);
  return out;
}

/** The sign atlas: every shop's sign in one texture, `ATLAS_COLS` across, each cell `CELL_W` x `CELL_H` px. */
const ATLAS_COLS = 5;
const CELL_W = 384;
const CELL_H = 96;

/** Where shop `i`'s sign sits in the atlas, as UV bounds [u0, v0, u1, v1]. */
export function atlasCell(i: number): [number, number, number, number] {
  const rows = Math.ceil(SIGNS.length / ATLAS_COLS);
  const col = i % ATLAS_COLS;
  const row = Math.floor(i / ATLAS_COLS);
  return [col / ATLAS_COLS, 1 - (row + 1) / rows, (col + 1) / ATLAS_COLS, 1 - row / rows];
}

/** Draws one shop's sign face into the cell at (x, y): its name big over what it sells, on its own colors. */
function drawSign(ctx: CanvasRenderingContext2D, shop: Shop, x: number, y: number): void {
  const k = CELL_W / 512;
  ctx.fillStyle = shop.ink;
  ctx.fillRect(x, y, CELL_W, CELL_H);
  ctx.fillStyle = shop.bg;
  ctx.fillRect(x + 6 * k, y + 6 * k, CELL_W - 12 * k, CELL_H - 12 * k);
  let left = 0;
  if (shop.cross) {
    // A plain green cross, the pharmacy sign everyone knows.
    ctx.fillStyle = shop.ink;
    ctx.fillRect(x + 34 * k, y + 40 * k, 48 * k, 48 * k);
    ctx.fillStyle = shop.bg;
    for (const [cx, cy] of [
      [34, 40],
      [66, 40],
      [34, 72],
      [66, 72],
    ] as const)
      ctx.fillRect(x + cx * k, y + cy * k, 16 * k, 16 * k);
    left = 50 * k;
  }
  ctx.fillStyle = shop.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const mid = x + CELL_W / 2 + left;
  const room = CELL_W - 40 * k - 2 * left;
  const big = Math.round(56 * k);
  ctx.font = `900 ${big}px system-ui, sans-serif`;
  const nameW = ctx.measureText(shop.name).width;
  if (nameW > room) ctx.font = `900 ${Math.floor((big * room) / nameW)}px system-ui, sans-serif`;
  ctx.fillText(shop.name, mid, y + 50 * k);
  ctx.font = `700 ${Math.round(24 * k)}px system-ui, sans-serif`;
  ctx.fillText(shop.line, mid, y + 100 * k);
}

/** A sign's ink for lettering on `bg`: near-black on a light board, cream on a dark one. */
export function inkFor(bg: string): string {
  const c = new THREE.Color(bg);
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b > 0.45 ? "#1d1d1d" : "#fff7e6";
}

/** One sign on its own texture (#233: a stand's name board), the street's look. Null where there's no canvas (tests). */
export function signTexture(shop: Shop): THREE.CanvasTexture | null {
  const ctx = canvas2d(CELL_W, CELL_H);
  if (!ctx) return null;
  drawSign(ctx, shop, 0, 0);
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Every shop's sign in one texture (`atlasCell` says where). Null where there's no canvas (tests). */
export function shopAtlasTexture(): THREE.CanvasTexture | null {
  const rows = Math.ceil(SIGNS.length / ATLAS_COLS);
  const ctx = canvas2d(CELL_W * ATLAS_COLS, CELL_H * rows);
  if (!ctx) return null;
  SIGNS.forEach((shop, i) => drawSign(ctx, shop, (i % ATLAS_COLS) * CELL_W, Math.floor(i / ATLAS_COLS) * CELL_H));
  const tex = new THREE.CanvasTexture(ctx.canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/**
 * The street's sign and awning materials: one sign material over the atlas,
 * so the street's merge (`mergeStatic`) draws every sign in one call, and one
 * awning material per color.
 */
export function shopMaterials(): { face: THREE.Material; awning: (shop: Shop) => THREE.Material } {
  const map = shopAtlasTexture();
  // Lit a little from within, like the hub signs, so it reads in the building's shadow.
  const face = toon(
    map ? "#ffffff" : "#e8e2d6",
    map ? { map, emissive: "#ffffff", emissiveMap: map, emissiveIntensity: 0.3, roughness: 0.6 } : { roughness: 0.6 },
    "paint",
  );
  const awnings = new Map<string, THREE.Material>();
  const awning = (shop: Shop) => {
    let m = awnings.get(shop.awning);
    if (!m) awnings.set(shop.awning, (m = toon(shop.awning, { roughness: 0.8 }, "cloth")));
    return m;
  };
  return { face, awning };
}
